import React, { FC, useEffect, useMemo } from 'react'
import { IStepComponentProps } from '../../types/IStepComponentProps'
import { Trans } from '@lingui/macro'

import Spinner, {
  SpinnerSizeEnum
} from '../../../../../components/common/spinner/Spinner'
import LinkButton from '../../../../../components/common/link/LinkButton'
import IconArrowLeft from '../../../../../components/common/icon/IconArrowLeft'
import IconTick from '../../../../../components/common/icon/IconTick'
import { staticLinks } from '../../../../../../../shared/linkBuilder'
import IconQuestion from '../../../../../components/common/icon/IconQuestion'
import { useUsersImportDetailsQueryWithParams } from '../../UsersImportDetails'
import { UsersImportStatusEnum } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { UsersImportResultFragment } from '../../../operations/UsersImportFragments.codegen'
import { Box, Heading } from '@chakra-ui/react'
import { brandColors } from 'shared/basicPalette'

interface IProps {
  result: UsersImportResultFragment
}

const ReturnLink: FC = () => (
  <LinkButton to={staticLinks.users}>
    <IconArrowLeft /> <Trans>Return to users list</Trans>
  </LinkButton>
)

const SuccessComponent: FC<IProps> = ({ result }) => {
  return (
    <>
      <Heading size="m">
        <Trans>User details import complete</Trans> <IconTick />
      </Heading>

      <ul>
        <li>
          <em>{result.stats?.recordsCreated || 0}</em>
          <Trans>new user accounts were created</Trans>
        </li>
        <li>
          <em>{result.stats?.recordsUpdated || 0}</em>
          <Trans>existing user accounts were updated</Trans>
        </li>
      </ul>

      {!!result.errors?.length ? (
        <Box
          backgroundColor={brandColors.yellow.mid}
          rounded={'4px'}
          p={'4px'}
          m={'2px'}
        >
          <Heading size="s">
            <Trans>Warnings</Trans>:
          </Heading>
          <ul>
            {result.errors?.map((error, idx) => (
              <li key={idx}>
                {error.message}{' '}
                {error.lineNumber && (
                  <em>
                    <Trans>at line</Trans> {error.lineNumber}
                  </em>
                )}
              </li>
            ))}
          </ul>
        </Box>
      ) : null}

      <ReturnLink />
    </>
  )
}

const ErrorComponent: FC<IProps> = ({ result }) => {
  return (
    <>
      <h3>
        <Trans>User details import failed</Trans> <IconQuestion />
      </h3>
      <ul>
        {result.errors?.map((error, idx) => (
          <li key={idx}>
            {error.message}{' '}
            {error.lineNumber && (
              <em>
                <Trans>at line</Trans> {error.lineNumber}
              </em>
            )}
          </li>
        ))}
      </ul>
      <ReturnLink />
    </>
  )
}

export const ResultsStepComponent: FC<IStepComponentProps> = ({
  usersImport
}) => {
  const { status } = usersImport
  const { refetch } = useUsersImportDetailsQueryWithParams()
  const isLoading = useMemo(
    () =>
      [
        UsersImportStatusEnum.PENDING,
        UsersImportStatusEnum.PROCESSING
      ].includes(status),
    [status]
  )

  useEffect(() => {
    if (!isLoading) {
      return
    }
    const timer = setInterval(() => refetch(), 5000)

    return () => clearInterval(timer)
  }, [refetch, isLoading])

  return (
    <div className="BulkUsersImportPage__step finished-step">
      {isLoading && (
        <>
          <h4 className="loading-caption">
            <Trans>We are processing your data...</Trans>
          </h4>
          <Spinner size={SpinnerSizeEnum.large} />
        </>
      )}
      {status === UsersImportStatusEnum.SUCCESS && (
        <SuccessComponent result={usersImport.result!} />
      )}
      {status === UsersImportStatusEnum.ERROR && (
        <ErrorComponent result={usersImport.result!} />
      )}
    </div>
  )
}
